import React, { Component } from "react";
import { Row, Col, Card, Spinner, Table, Badge, ButtonGroup, Button } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import * as Cookies from "es-cookie";

// Cohort / batch reports for an institution ("batch" = a section within a branch):
//  - batch-wise average score comparison across subjects
//  - class-wide weak-area heatmap (chapter x section)
//  - test-wise batch performance trend
const SUBJECTS_QUERY = gql`
  query ($institution_id: Int!, $exam_type: Int, $branch_ids: [Int], $section_ids: [Int], $class_ids: [Int], $from_date: String, $to_date: String) {
    getBatchSubjectComparison(institution_id: $institution_id, exam_type: $exam_type, branch_ids: $branch_ids, section_ids: $section_ids, class_ids: $class_ids, from_date: $from_date, to_date: $to_date) {
      section_id
      section_name
      branch_name
      total_students
      avg_score
      subjects { subject_id subject avg_score avg_accuracy students }
    }
  }
`;
const HEATMAP_QUERY = gql`
  query ($institution_id: Int!, $exam_type: Int, $branch_ids: [Int], $section_ids: [Int], $class_ids: [Int], $threshold: Float, $from_date: String, $to_date: String) {
    getBatchWeakAreaHeatmap(institution_id: $institution_id, exam_type: $exam_type, branch_ids: $branch_ids, section_ids: $section_ids, class_ids: $class_ids, threshold: $threshold, from_date: $from_date, to_date: $to_date) {
      threshold
      sections { section_id section_name branch_name }
      rows { subject_id subject chapter_id chapter cells { section_id students weak_students weak_pct avg_accuracy } }
    }
  }
`;
const TREND_QUERY = gql`
  query ($institution_id: Int!, $exam_type: Int, $branch_ids: [Int], $section_ids: [Int], $class_ids: [Int], $from_date: String, $to_date: String) {
    getBatchExamTrend(institution_id: $institution_id, exam_type: $exam_type, branch_ids: $branch_ids, section_ids: $section_ids, class_ids: $class_ids, from_date: $from_date, to_date: $to_date) {
      section_id
      section_name
      branch_name
      trend
      slope
      exam_count
      points { exam_paper_id exam_name date avg_score students }
    }
  }
`;

const QUERIES = { subjects: SUBJECTS_QUERY, heatmap: HEATMAP_QUERY, trend: TREND_QUERY };
const RESULT_KEY = {
    subjects: "getBatchSubjectComparison",
    heatmap: "getBatchWeakAreaHeatmap",
    trend: "getBatchExamTrend",
};
const TREND_VARIANT = { improving: "success", declining: "danger", stable: "secondary" };
const CHART_COLORS = ["#077EE6", "#27AE60", "#F2994A", "#9B51E0", "#EB5757", "#2D9CDB", "#16A085", "#E67E22"];

// green (low weakness) -> yellow -> red (high weakness)
const heatColor = (pct) => {
    if (pct == null) return "#f5f5f5";
    const p = Math.max(0, Math.min(100, pct)) / 100;
    const r = p < 0.5 ? Math.round(46 + (242 - 46) * (p / 0.5)) : 242;
    const g = p < 0.5 ? 174 : Math.round(174 - (174 - 60) * ((p - 0.5) / 0.5));
    return `rgba(${r}, ${g}, 70, 0.85)`;
};

class BatchReportsCollege extends Component {
    constructor(props) {
        super(props);
        this.state = { view: "subjects", loading: false, error: null, subjects: null, heatmap: null, trend: null };
    }

    componentDidMount() {
        this.fetchView(this.state.view);
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.institution_id !== this.props.institution_id ||
            prevProps.exam_type !== this.props.exam_type ||
            prevProps.from_date !== this.props.from_date ||
            prevProps.to_date !== this.props.to_date
        ) {
            this.setState({ subjects: null, heatmap: null, trend: null }, () => this.fetchView(this.state.view));
        }
    }

    variables() {
        const institution_id = Number(this.props.institution_id) || parseInt(Cookies.get("institutionid"));
        return {
            institution_id,
            exam_type: this.props.exam_type != null ? Number(this.props.exam_type) : null,
            branch_ids: this.props.branch_ids && this.props.branch_ids.length ? this.props.branch_ids : null,
            section_ids: this.props.section_ids && this.props.section_ids.length ? this.props.section_ids : null,
            class_ids: this.props.class_ids && this.props.class_ids.length ? this.props.class_ids : null,
            threshold: 50,
            from_date: this.props.from_date != null ? String(this.props.from_date) : null,
            to_date: this.props.to_date != null ? String(this.props.to_date) : null,
        };
    }

    fetchView = async (view) => {
        this.setState({ loading: true, error: null });
        try {
            const res = await this.props.client.query({
                query: QUERIES[view],
                variables: this.variables(),
                fetchPolicy: "network-only",
            });
            const fallback = view === "heatmap" ? { sections: [], rows: [] } : [];
            this.setState({ [view]: res?.data?.[RESULT_KEY[view]] || fallback, loading: false });
        } catch (e) {
            this.setState({ error: e.message || "Failed to load report", loading: false });
        }
    };

    setView = (view) => {
        if (view === this.state.view) return;
        this.setState({ view }, () => {
            if (!this.state[view]) this.fetchView(view);
        });
    };

    // ---------- Subject comparison ----------
    renderSubjects() {
        const data = this.state.subjects || [];
        if (!data.length) return <p className="text-muted mb-0">No subject data for the current filters.</p>;

        const subjectNames = [];
        data.forEach((s) => (s.subjects || []).forEach((su) => {
            if (!subjectNames.includes(su.subject)) subjectNames.push(su.subject);
        }));

        const chartOptions = {
            chart: { type: "column", height: 300 },
            title: { text: null },
            credits: { enabled: false },
            xAxis: { categories: data.slice(0, 15).map((s) => s.section_name), labels: { rotation: -45 } },
            yAxis: { title: { text: "Avg Subject Score" } },
            tooltip: { shared: true },
            series: subjectNames.map((name, i) => ({
                name,
                color: CHART_COLORS[i % CHART_COLORS.length],
                data: data.slice(0, 15).map((s) => {
                    const su = (s.subjects || []).find((x) => x.subject === name);
                    return su ? su.avg_score : null;
                }),
            })),
        };

        return (
            <>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                <Table size="sm" bordered responsive className="mt-3 mb-0">
                    <thead>
                        <tr>
                            <th>Batch (Section)</th>
                            <th>Branch</th>
                            <th className="text-center">Students</th>
                            <th className="text-center">Avg Score</th>
                            {subjectNames.map((n) => <th key={n} className="text-center">{n}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((s) => (
                            <tr key={s.section_id}>
                                <td>{s.section_name}</td>
                                <td>{s.branch_name}</td>
                                <td className="text-center">{s.total_students}</td>
                                <td className="text-center font-weight-bold">{s.avg_score}</td>
                                {subjectNames.map((n) => {
                                    const su = (s.subjects || []).find((x) => x.subject === n);
                                    return (
                                        <td key={n} className="text-center">
                                            {su ? <>{su.avg_score} <span className="text-muted small">({su.avg_accuracy}%)</span></> : "—"}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <p className="small text-muted mt-1 mb-0">Subject cells show avg score with accuracy in brackets. Chart shows the first 15 sections.</p>
            </>
        );
    }

    // ---------- Weak-area heatmap ----------
    renderHeatmap() {
        const hm = this.state.heatmap;
        if (!hm || !hm.rows || !hm.rows.length) return <p className="text-muted mb-0">No weak-area data for the current filters.</p>;
        const { sections, rows, threshold } = hm;
        return (
            <>
                <p className="small text-muted mb-2">
                    Each cell = % of the batch scoring below {threshold}% accuracy in that chapter. Redder = weaker. Showing the 40 weakest chapters.
                </p>
                <div style={{ overflowX: "auto" }}>
                    <Table size="sm" bordered className="mb-0" style={{ minWidth: 700 }}>
                        <thead>
                            <tr>
                                <th style={{ position: "sticky", left: 0, background: "#fff", minWidth: 180 }}>Chapter</th>
                                {sections.map((sec) => (
                                    <th key={sec.section_id} className="text-center small" title={sec.branch_name}>
                                        {sec.section_name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const cellBySection = {};
                                (row.cells || []).forEach((c) => { cellBySection[c.section_id] = c; });
                                return (
                                    <tr key={`${row.subject_id}-${row.chapter_id}`}>
                                        <td style={{ position: "sticky", left: 0, background: "#fff" }}>
                                            <span className="small">{row.chapter}</span>
                                            <div className="text-muted" style={{ fontSize: "10px" }}>{row.subject}</div>
                                        </td>
                                        {sections.map((sec) => {
                                            const c = cellBySection[sec.section_id];
                                            return (
                                                <td
                                                    key={sec.section_id}
                                                    className="text-center"
                                                    style={{ background: c ? heatColor(c.weak_pct) : "#f5f5f5", color: c && c.weak_pct > 55 ? "#fff" : "#222" }}
                                                    title={c ? `${c.weak_students}/${c.students} weak · avg ${c.avg_accuracy}%` : "no data"}
                                                >
                                                    {c ? `${c.weak_pct}%` : ""}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </>
        );
    }

    // ---------- Exam trend ----------
    renderTrend() {
        const data = this.state.trend || [];
        if (!data.length) return <p className="text-muted mb-0">No exam trend data for the current filters.</p>;

        const top = [...data].sort((a, b) => b.exam_count - a.exam_count).slice(0, 8);
        const maxPoints = Math.max(...top.map((s) => (s.points || []).length), 0);
        const chartOptions = {
            chart: { type: "line", height: 320 },
            title: { text: null },
            credits: { enabled: false },
            xAxis: { categories: Array.from({ length: maxPoints }, (_, i) => `T${i + 1}`), title: { text: "Test (chronological)" } },
            yAxis: { title: { text: "Batch Avg Score" } },
            tooltip: { shared: false },
            series: top.map((s, i) => ({
                name: s.section_name,
                color: CHART_COLORS[i % CHART_COLORS.length],
                data: (s.points || []).map((p) => p.avg_score),
            })),
        };

        return (
            <>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                <Table size="sm" bordered responsive className="mt-3 mb-0">
                    <thead>
                        <tr>
                            <th>Batch (Section)</th>
                            <th>Branch</th>
                            <th className="text-center">Tests</th>
                            <th className="text-center">Trend</th>
                            <th className="text-center">Slope /test</th>
                            <th className="text-center">First → Last Avg</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((s) => {
                            const pts = s.points || [];
                            const first = pts.length ? pts[0].avg_score : null;
                            const last = pts.length ? pts[pts.length - 1].avg_score : null;
                            return (
                                <tr key={s.section_id}>
                                    <td>{s.section_name}</td>
                                    <td>{s.branch_name}</td>
                                    <td className="text-center">{s.exam_count}</td>
                                    <td className="text-center">
                                        <Badge variant={TREND_VARIANT[s.trend] || "secondary"}>{s.trend}</Badge>
                                    </td>
                                    <td className="text-center">{s.slope >= 0 ? "+" : ""}{s.slope}</td>
                                    <td className="text-center">{first != null ? `${first} → ${last}` : "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <p className="small text-muted mt-1 mb-0">Chart shows the 8 sections with the most tests.</p>
            </>
        );
    }

    render() {
        const { view, loading, error } = this.state;
        const tabs = [
            { key: "subjects", label: "Subject Comparison" },
            { key: "heatmap", label: "Weak-Area Heatmap" },
            { key: "trend", label: "Exam Trend" },
        ];
        return (
            <Card className="mt-4 mb-3">
                <Card.Body>
                    <Row className="align-items-center mb-3">
                        <Col xs={12} md>
                            <h5 className="mb-2 mb-md-0 font-weight-bold">Batch / Cohort Reports</h5>
                        </Col>
                        <Col xs="auto">
                            <ButtonGroup size="sm">
                                {tabs.map((t) => (
                                    <Button
                                        key={t.key}
                                        variant={view === t.key ? "primary" : "outline-primary"}
                                        onClick={() => this.setView(t.key)}
                                    >
                                        {t.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : error ? (
                        <span className="text-danger">{error}</span>
                    ) : view === "subjects" ? (
                        this.renderSubjects()
                    ) : view === "heatmap" ? (
                        this.renderHeatmap()
                    ) : (
                        this.renderTrend()
                    )}
                </Card.Body>
            </Card>
        );
    }
}

export default withApollo(BatchReportsCollege);
